// Family Account, code redemption and event eggs (AUTH-BACKEND.md §1,
// EVENT-CODES.md §3).
//
// Guest-first is law and this panel says so out loud: the first thing a player
// reads here is that the whole game works without an account. The email asked
// for is explicitly a grown-up's.

import type { GameStateStore } from '../core/store';
import { getLocale, pick, t } from '../i18n';
import { getItem } from '../sim/items';
import type { GrantResult } from '../sim/eventBirds';
import type { SupabaseClient } from '../net/supabase';
import { accountsEnabled } from '../net/supabase';
import { redeemCode } from '../net/redemption';
import { resolveConflict, syncSave, upgradeGuestSave } from '../net/cloudSync';
import { formatCode } from '../sim/eventCodes';
import { hoursUntilHatch, startIncubation } from '../sim/eventBirds';
import { el, escapeHtml, modal } from './dom';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A control an account surface may render, in the order it renders it. */
export type AccountControl =
  | 'guestNote'
  | 'email'
  | 'sendLink'
  | 'redeem'
  | 'signedInAs'
  | 'sync'
  | 'signOut'
  | 'delete';

/**
 * Every control this build is allowed to put in front of a player, decided in
 * one place. With accounts switched off there is nothing to create and nothing
 * to delete, so the list is short by construction — a new button cannot appear
 * on an account surface without someone editing it here, and this function is
 * what the test reads.
 *
 * Deletion is gated with creation on purpose: an account surface a child can
 * reach is how a parent's account gets deleted from a shared phone, and there
 * is no account to delete in a build that cannot make one.
 */
export function accountControls(options: { enabled: boolean; signedIn: boolean }): AccountControl[] {
  if (!options.enabled) return ['guestNote'];
  return options.signedIn
    ? ['guestNote', 'signedInAs', 'sync', 'signOut', 'delete']
    : ['guestNote', 'email', 'sendLink', 'redeem'];
}

/** A code can now grant four different things, and each reads differently. */
function grantMessage(granted: GrantResult, eventLabel: string): string {
  switch (granted.kind) {
    case 'egg':
      // Breed ids are shown raw everywhere else in the UI; no breed.* table
      // exists yet, and inventing one here would only render the key.
      return t('redeem.success', { breed: granted.egg.breed, event: eventLabel });
    case 'bird':
      return t('redeem.successBird', {
        name: granted.bird.name,
        stage: t(`flock.stage.${granted.bird.stage}`),
        event: eventLabel,
      });
    case 'item':
      return t('redeem.successItem', {
        item: pick(getItem(granted.itemId)?.name ?? { en: granted.itemId, es: granted.itemId }),
        count: granted.amount,
      });
    case 'eggits':
      return t('redeem.successEggits', { count: granted.amount });
  }
}

export function openAccountPanel(
  host: HTMLElement,
  store: GameStateStore,
  client: SupabaseClient,
  onStatus: (message: string) => void,
): void {
  const { body } = modal(host, t('account.title'));

  const render = (): void => {
    body.replaceChildren();

    const controls = accountControls({
      enabled: accountsEnabled(),
      signedIn: client.signedIn,
    });

    // The reassurance comes first, before any ask.
    body.appendChild(el('p', 'mb-3 text-sm', escapeHtml(t('account.guestNote'))));

    if (!client.configured || (!controls.includes('email') && !controls.includes('sync'))) {
      // Switched off in this build, or simply not configured. Same shape either
      // way — a child meets no email field and no delete button in both, so the
      // reason is a build detail rather than an invitation to keep tapping.
      body.appendChild(
        el('p', 'rounded-xl bg-black/20 p-3 text-sm', escapeHtml(t('account.notConfigured'))),
      );
      return;
    }

    if (controls.includes('sync')) {
      body.appendChild(
        el(
          'p',
          'mb-3 rounded-xl bg-black/20 p-3 text-sm',
          escapeHtml(t('account.signedInAs', { email: client.email ?? '' })),
        ),
      );

      const row = el('div', 'flex flex-wrap gap-2');
      const sync = el('button', 'cf-btn px-4 text-xs', escapeHtml(t('sync.uploaded')));
      sync.textContent = t('common.done');
      sync.addEventListener('click', () => void runSync());
      const out = el('button', 'cf-btn bg-black/40 px-4 text-xs', escapeHtml(t('account.signOut')));
      out.addEventListener('click', () => {
        client.signOut();
        render();
      });

      // The parent's deletion right, on the same surface that can create an
      // account — so it is only ever reachable where creation is.
      const del = el('button', 'cf-btn bg-rose-800 px-4 text-xs', escapeHtml(t('account.deleteData')));
      del.addEventListener('click', () => {
        if (!window.confirm(t('account.deleteConfirm'))) return;
        void client.deleteAccountData().then((result) => {
          if (result.ok) {
            client.signOut();
            onStatus(t('account.deleted'));
            render();
          } else {
            onStatus(t('code.server'));
          }
        });
      });

      row.append(sync, out, del);
      body.appendChild(row);
      return;
    }

    body.appendChild(el('label', 'mb-1 block text-xs font-semibold', escapeHtml(t('account.emailLabel'))));
    const input = el('input', 'cf-chip w-full px-3 py-2 text-sm') as HTMLInputElement;
    input.type = 'email';
    input.autocomplete = 'email';
    input.setAttribute('aria-label', t('account.emailLabel'));
    body.appendChild(input);
    body.appendChild(el('p', 'mt-1 text-xs opacity-70', escapeHtml(t('account.emailHint'))));

    const send = el('button', 'cf-btn mt-3 px-4 text-xs', escapeHtml(t('account.sendLink')));
    send.addEventListener('click', () => {
      if (!EMAIL_PATTERN.test(input.value.trim())) {
        onStatus(t('account.badEmail'));
        return;
      }
      void client.sendMagicLink(input.value.trim(), getLocale()).then((result) => {
        onStatus(result.ok ? t('account.linkSent') : t('code.server'));
      });
    });
    body.appendChild(send);
  };

  const runSync = async (): Promise<void> => {
    const outcome = await syncSave(store, client);
    if (outcome.status === 'conflict') {
      openConflict(host, store, client, outcome.local, outcome.remote, onStatus);
      return;
    }
    const messages: Record<string, string> = {
      uploaded: t('sync.uploaded'),
      downloaded: t('sync.downloaded'),
      in_sync: t('sync.inSync'),
    };
    onStatus(messages[outcome.status] ?? t('sync.offline'));
  };

  render();
}

/** Two real farms, one account. The player picks; the loser is kept. */
function openConflict(
  host: HTMLElement,
  store: GameStateStore,
  client: SupabaseClient,
  local: Parameters<typeof resolveConflict>[3],
  remote: Parameters<typeof resolveConflict>[3],
  onStatus: (message: string) => void,
): void {
  const { body, close } = modal(host, t('sync.conflictTitle'));
  body.appendChild(el('p', 'mb-3 text-sm', escapeHtml(t('sync.conflictBody'))));

  const summary = (state: typeof local, key: 'sync.localSummary' | 'sync.remoteSummary') =>
    t(key, {
      day: Math.floor(state.simHour / 24),
      birds: state.flock.length,
      eggits: state.eggits,
    });

  const choose = (choice: 'keep_local' | 'keep_remote', label: string) => {
    const btn = el('button', 'cf-btn mb-2 w-full px-4 py-3 text-left text-xs', escapeHtml(label));
    btn.addEventListener('click', () => {
      void resolveConflict(store, client, choice, remote).then(() => {
        onStatus(choice === 'keep_local' ? t('sync.uploaded') : t('sync.downloaded'));
        close();
      });
    });
    return btn;
  };

  body.appendChild(el('p', 'mb-1 text-xs opacity-80', escapeHtml(summary(local, 'sync.localSummary'))));
  body.appendChild(choose('keep_local', t('sync.keepLocal')));
  body.appendChild(el('p', 'mb-1 text-xs opacity-80', escapeHtml(summary(remote, 'sync.remoteSummary'))));
  body.appendChild(choose('keep_remote', t('sync.keepRemote')));
}

export function openRedeemPanel(
  host: HTMLElement,
  store: GameStateStore,
  client: SupabaseClient,
  onStatus: (message: string) => void,
  prefill = '',
): void {
  const { body } = modal(host, t('redeem.title'));
  // The fundraising promise is part of the product, so it is stated up front.
  body.appendChild(el('p', 'mb-3 text-sm', escapeHtml(t('redeem.blurb'))));

  // Codes are an online feature like any other, so the same switch covers them:
  // with accounts off there is no account to redeem against.
  if (!accountsEnabled()) {
    body.appendChild(el('p', 'rounded-xl bg-black/20 p-3 text-sm', escapeHtml(t('redeem.unavailable'))));
    return;
  }

  const input = el('input', 'cf-chip w-full px-3 py-2 text-center text-lg tracking-widest') as HTMLInputElement;
  input.placeholder = t('redeem.placeholder');
  input.value = prefill ? formatCode(prefill) : '';
  input.setAttribute('aria-label', t('redeem.title'));
  body.appendChild(input);

  const message = el('p', 'mt-2 text-sm');
  const submit = el('button', 'cf-btn mt-3 px-4 text-xs', escapeHtml(t('redeem.submit')));
  submit.addEventListener('click', () => {
    submit.disabled = true;
    void redeemCode(store, client, input.value).then((outcome) => {
      submit.disabled = false;
      // Green vs red is not a signal on its own; the glyph is what a
      // colour-blind reader actually sees (UI-DESIGN §6).
      if (outcome.status === 'redeemed') {
        const text = grantMessage(outcome.granted, outcome.payload.eventLabel);
        message.className = 'mt-2 text-sm text-emerald-300';
        message.textContent = `✓ ${text}`;
        onStatus(text);
        return;
      }
      message.className = 'mt-2 text-sm text-rose-300';
      if (outcome.status === 'rejected') message.textContent = `✕ ${t(`code.${outcome.reason}`)}`;
      else if (outcome.status === 'failed') message.textContent = `✕ ${t(`code.${outcome.reason}`)}`;
      else if (outcome.status === 'needs_account')
        message.textContent = `✕ ${t('redeem.needsAccount')}`;
      else message.textContent = `✕ ${t('redeem.unavailable')}`;
    });
  });

  body.append(submit, message);
}

/** The eggs a player has redeemed but not yet hatched. */
export function openEventEggs(
  host: HTMLElement,
  store: GameStateStore,
  onStatus: (message: string) => void,
): void {
  const { body } = modal(host, t('egg.title'));

  const render = (): void => {
    const state = store.getState();
    body.replaceChildren();
    const eggs = state.eventEggs ?? [];
    if (eggs.length === 0) {
      body.appendChild(el('p', 'text-sm opacity-70', escapeHtml(t('egg.none'))));
      return;
    }

    for (const egg of eggs) {
      const card = el('div', 'mb-2 rounded-xl bg-black/20 p-3');
      card.appendChild(el('div', 'text-sm font-semibold', escapeHtml(egg.breed)));
      card.appendChild(
        el('div', 'text-xs opacity-70', escapeHtml(t('egg.from', { event: egg.eventLabel }))),
      );

      const remaining = hoursUntilHatch(egg, state.simHour);
      if (remaining === null) {
        const incubate = el('button', 'cf-btn mt-2 px-3 text-xs', escapeHtml(t('egg.incubate')));
        incubate.addEventListener('click', () => {
          let started = false;
          store.mutate((s) => {
            started = startIncubation(s, egg.id);
          });
          onStatus(started ? t('egg.incubate') : t('egg.needsBrooder'));
          render();
        });
        card.appendChild(incubate);
      } else {
        card.appendChild(
          el(
            'div',
            'mt-2 text-xs',
            escapeHtml(t('egg.hatchingIn', { days: Math.ceil(remaining / 24) })),
          ),
        );
      }
      body.appendChild(card);
    }
  };

  render();
}

/** Called at boot after a magic-link return, to pull the guest save up. */
export async function completeSignIn(
  store: GameStateStore,
  client: SupabaseClient,
  onStatus: (message: string) => void,
): Promise<void> {
  const outcome = await upgradeGuestSave(store, client);
  if (outcome.status === 'uploaded') onStatus(t('sync.uploaded'));
  else if (outcome.status === 'downloaded') onStatus(t('sync.downloaded'));
}
